import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, GraduationCap, Activity, ClipboardCheck, CalendarClock, UserCheck, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Tile = ({ icon: Icon, label, value, sublabel, colour, testId }) => (
  <div
    className={`rounded-xl p-4 shadow-sm border border-white/20 ${colour}`}
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-2">
      <Icon size={22} className="opacity-90" />
      <span className="text-xs uppercase tracking-wide opacity-80">{label}</span>
    </div>
    <p className="text-3xl font-bold leading-tight">{value}</p>
    {sublabel && <p className="text-xs opacity-80 mt-1">{sublabel}</p>}
  </div>
);

const PrincipalTiles = () => {
  const [tiles, setTiles] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/head-teacher/principal-tiles`);
        setTiles(res.data);
      } catch (e) {
        console.error('principal tiles load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6" data-testid="principal-tiles-loading">
        <Loader2 className="animate-spin text-purple-500" size={24} />
      </div>
    );
  }

  if (!tiles) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="principal-tiles">
      <Tile
        icon={Users}
        label="Students"
        value={tiles.students_total || 0}
        sublabel="Total enrolled"
        colour="bg-gradient-to-br from-blue-500 to-blue-600 text-white"
        testId="tile-students-total"
      />
      <Tile
        icon={GraduationCap}
        label="Teachers"
        value={tiles.teachers_total || 0}
        sublabel="Teaching staff"
        colour="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white"
        testId="tile-teachers-total"
      />
      <Tile
        icon={Activity}
        label="Daily Active"
        value={`${tiles.daily_active_students || 0}`}
        sublabel={`${tiles.daily_active_pct || 0}% engaged today`}
        colour="bg-gradient-to-br from-orange-500 to-orange-600 text-white"
        testId="tile-daily-active"
      />
      <Tile
        icon={ClipboardCheck}
        label="Homework"
        value={`${tiles.homework_completion_pct || 0}%`}
        sublabel={`${tiles.homework_completed_last_7d || 0}/${tiles.homework_assigned_last_7d || 0} in 7 days`}
        colour="bg-gradient-to-br from-purple-500 to-purple-600 text-white"
        testId="tile-homework-completion"
      />
      <Tile
        icon={UserCheck}
        label="Teacher Activity"
        value={`${tiles.active_teachers_last_7d || 0}`}
        sublabel={`${tiles.teacher_activity_pct || 0}% active this week`}
        colour="bg-gradient-to-br from-pink-500 to-pink-600 text-white"
        testId="tile-teacher-activity"
      />
      <Tile
        icon={CalendarClock}
        label="Upcoming Exams"
        value={tiles.upcoming_exams_14d || 0}
        sublabel="Next 14 days"
        colour="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white"
        testId="tile-upcoming-exams"
      />
    </div>
  );
};

export default PrincipalTiles;
